/**
 * Per-worktree dev isolation for **browser cookies**.
 *
 * In local development every git worktree runs on its own Next.js port and its
 * own Supabase stack/DB (see scripts/dev-slots.ts). But every slot's app is
 * served from `localhost`, and the port is NOT part of a cookie's scope — so
 * all slots share ONE cookie jar. With the library-default cookie names, the
 * Supabase auth cookie (`sb-<host>-auth-token`) and the `enabled_modules` cookie
 * have the SAME name in every slot, so logging into one slot overwrites the
 * other slots' cookies. Because each slot is a different DB, that foreign token
 * is invalid there → you appear logged out (and can trip an orphaned-session
 * retry loop).
 *
 * The fix: when a dev slot is known, suffix those cookie names with the slot so
 * each slot keeps its own session and app-enablement state, side by side.
 *
 * `scripts/setup-worktree.ts` writes `NEXT_PUBLIC_DEV_SLOT=<n>` into each
 * worktree's generated `.env.local` (slot ≥ 1). It is ABSENT in production and
 * in the main checkout (slot 0), where these helpers fall back to the library
 * defaults — so production behavior (single domain, single cookie) is unchanged,
 * and the main checkout's default names stay distinct from every worktree's
 * `…_s<n>` names. This is a dev-only concern; the var never exists in prod.
 *
 * Deliberately free of any imports so it stays cheap to pull into the edge
 * middleware runtime.
 */

/** The current dev slot, or `undefined` outside per-worktree dev isolation. */
export const DEV_SLOT = process.env.NEXT_PUBLIC_DEV_SLOT || undefined

/**
 * Suffix to append to per-user dev cookies (e.g. `_s3`), or `''` when no dev
 * slot is set (production / main checkout → library-default names).
 */
export function devSlotSuffix(): string {
  return DEV_SLOT ? `_s${DEV_SLOT}` : ''
}

/**
 * The Supabase auth cookie name / storageKey for this slot, or `undefined` to
 * let `@supabase/ssr` derive its default from the host. MUST be passed to ALL
 * Supabase clients (browser, server, middleware) so they agree on the cookie —
 * if they disagree, SSR can't read the session the browser wrote.
 */
export function supabaseAuthCookieName(): string | undefined {
  return DEV_SLOT ? `sb-slot${DEV_SLOT}-auth-token` : undefined
}
